/**
 * ENG-3716 — pure mapping from the waiting-for-video-track state machine
 * (livekit/track-wait.ts) to a badge status.
 *
 * Kept in its own dependency-light module (enum + type imports only) so the
 * failure-mode mapping is unit-testable without the renderer environment.
 * Per rule: think-in-systems (ai.knowledge/global).
 */
import { BadgeStatus } from './types.js';
import type { VideoTrackWaitState } from '#~/livekit/track-wait.js';
import type { T_Maybe } from '@tryiris-ai/js-types';

/**
 * Resolve the badge status for a source whose video track has not (yet)
 * subscribed. A failed wait yields a concrete, cause-keyed failure badge; any
 * other state (waiting, untracked, or subscribed-but-track-not-yet-routed)
 * keeps the transient "Connecting..." badge.
 */
export const badgeStatusForVideoTrackWait = (
  waitState: T_Maybe<VideoTrackWaitState>
): BadgeStatus => {
  if (waitState?.phase === 'failed') {
    switch (waitState.cause) {
      case 'camera-unreachable':
        return BadgeStatus.BADGE_STATUS_VIDEO_CAMERA_UNREACHABLE;
      case 'stream-interrupted':
        return BadgeStatus.BADGE_STATUS_VIDEO_STREAM_INTERRUPTED;
      case 'unsupported-video-format':
        return BadgeStatus.BADGE_STATUS_VIDEO_FORMAT_UNSUPPORTED;
      case 'device-not-discovered':
        return BadgeStatus.BADGE_STATUS_VIDEO_DEVICE_NOT_DISCOVERED;
      case 'h265-unsupported':
        return BadgeStatus.BADGE_STATUS_VIDEO_H265_UNSUPPORTED;
    }
  }

  return BadgeStatus.BADGE_STATUS_CONNECTING;
};
